/**
 * Phase 86 Plan 86-03 Task 2 — the Compost design's per-zone action chip
 * (+LOG / TOUCH / REVIVE, plus the disabled dashed +LOG on terminal rows).
 *
 * Always a real `<button>` — never a styled `<span>` — so it is keyboard
 * reachable and announced correctly. `stopPropagation` on click keeps a
 * chip click from also triggering the row's own navigation (README
 * "Interactions & behavior" — "Action chip click — stopPropagation, then:
 * ..."). The accessible name always names both the action and the task
 * code, e.g. "Log time on CSOB-318" (README "Accessibility" section).
 */
import type { MouseEvent } from 'react';
import { ZONE_CHIP_STYLE } from './compostTokens';
import type { TaskRotZone } from '../../utils/format';

function actionVerb(label: string): string {
  const upper = label.trim().toUpperCase();
  if (upper === '+LOG') return 'Log time on';
  if (upper === 'TOUCH') return 'Touch';
  if (upper === 'REVIVE') return 'Revive';
  return upper;
}

export interface MtActionChipProps {
  /** Visible chip text: "+LOG", "TOUCH" or "REVIVE". */
  label: string;
  /** Drives the chip's zone-coloured border and text. */
  zone: TaskRotZone;
  taskCode: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Terminal rows render the disabled dashed +LOG variant. */
  disabled?: boolean;
  className?: string;
}

export function MtActionChip({
  label,
  zone,
  taskCode,
  onClick,
  disabled = false,
  className = '',
}: MtActionChipProps) {
  const cfg = ZONE_CHIP_STYLE[zone];
  const dashed = disabled || zone === 'terminal';
  const accessibleName = `${actionVerb(label)} ${taskCode}`;

  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (disabled) return;
    onClick(e);
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={handleClick}
      aria-label={accessibleName}
      data-testid="mt-action-chip"
      className={`inline-flex items-center justify-center px-2.5 py-1 rounded font-mono text-[10px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ms-orange disabled:cursor-not-allowed ${
        dashed ? 'border border-dashed' : 'border'
      } ${className}`}
      style={{
        borderColor: cfg.borderHex,
        color: cfg.textHex,
        backgroundColor: cfg.backgroundHex ?? '#fff',
      }}
    >
      {label}
    </button>
  );
}
